import { createPortal } from "react-dom";
import { useSyncExternalStore } from "react";
import type { ReactNode } from "react";

/**
 * [portal pilot] React-in-SlickGrid-cell via a SINGLE managed root instead of a
 * `createRoot` per cell.
 *
 * Why: `createRoot(cellNode)` stands up an independent React root (FiberRoot +
 * reconciler + event delegation) per cell. SlickGrid recycles cell DOM on every
 * scroll, orphaning those roots — and they only free if you manually
 * `unmount()`, which never happens → linear memory growth. Rendering the same
 * content through `createPortal` from ONE always-mounted host means React owns
 * the lifecycle: when a cell's container leaves the document we drop it from the
 * map and React unmounts that portal automatically. The host is mounted under
 * the app providers, so portal children inherit Redux/theme/i18n context — no
 * per-cell `<ProviderApp>`.
 */

interface Entry {
  key: number;
  node: ReactNode;
  createdAt: number;
}

// Containers younger than this are assumed to be mid-attach by SlickGrid and are
// never swept (avoids unmounting a cell a tick before it's inserted).
const GRACE_MS = 2000;

const entries = new Map<HTMLElement, Entry>();
const listeners = new Set<() => void>();
let version = 0;
let keySeq = 0;

const emit = () => {
  version++;
  listeners.forEach((l) => l());
};
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const getSnapshot = () => version;

let sweepScheduled = false;
const idle: (cb: () => void) => void =
  typeof requestIdleCallback === "function"
    ? (cb) => {
        requestIdleCallback(() => cb(), { timeout: 1000 });
      }
    : (cb) => {
        setTimeout(cb, 300);
      };

function scheduleSweep(): void {
  if (sweepScheduled) return;
  sweepScheduled = true;
  idle(() => {
    sweepScheduled = false;
    sweepDisconnectedPortals();
    // keep sweeping while recycled containers remain (fling / navigation)
    for (const el of entries.keys()) {
      if (!el.isConnected) {
        scheduleSweep();
        break;
      }
    }
  });
}

/**
 * Render `node` into `container` via a portal owned by the host. Drop-in for
 * `const r = createRoot(container); r.render(node); return container;`.
 */
export function mountCellPortal(container: HTMLElement, node: ReactNode): HTMLElement {
  const existing = entries.get(container);
  entries.set(container, {
    key: existing ? existing.key : ++keySeq,
    node,
    createdAt: existing ? existing.createdAt : Date.now(),
  });
  emit();
  scheduleSweep();
  return container;
}

/** Remove a specific cell's portal (React unmounts it on the next commit). */
export function unmountCellPortal(container: HTMLElement): void {
  if (entries.delete(container)) emit();
}

/**
 * `createRoot`-compatible shim backed by the single portal host. Drop-in for
 * react-dom's `createRoot`, so existing `const r = createRoot(el); r.render(x)`
 * call sites render through the ONE app root (shared context, auto-unmount on
 * recycle) instead of spinning up a detached React root per cell. Import aliased:
 *   import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
 */
export function portalCreateRoot(container: HTMLElement) {
  return {
    render(node: ReactNode) {
      mountCellPortal(container, node);
    },
    unmount() {
      unmountCellPortal(container);
    },
  };
}

/** Drop portals whose container has left the document → React unmounts them. */
export function sweepDisconnectedPortals(): void {
  const now = Date.now();
  let changed = false;
  for (const [el, e] of entries) {
    if (!el.isConnected && now - e.createdAt > GRACE_MS) {
      entries.delete(el);
      changed = true;
    }
  }
  if (changed) emit();
}

/** Mounted ONCE under the app providers. Renders one portal per live cell. */
export function CellPortalHost(): ReactNode {
  useSyncExternalStore(subscribe, getSnapshot);
  const portals: ReactNode[] = [];
  for (const [el, entry] of entries) {
    portals.push(createPortal(entry.node, el, String(entry.key)));
  }
  return <>{portals}</>;
}

/** Diagnostics for the perf probe. */
export function __cellPortalStats() {
  let connected = 0;
  for (const el of entries.keys()) if (el.isConnected) connected++;
  return { total: entries.size, connected, detached: entries.size - connected };
}

declare global {
  interface Window {
    /** Set below so the probe is callable from the devtools console. */
    __cellPortalStats?: typeof __cellPortalStats;
  }
}

if (typeof window !== "undefined") {
  window.__cellPortalStats = __cellPortalStats;
}
